import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function ServiceAgile() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('agile-scrum-coach');
        setPageData(page);
      } catch (err) {
        console.error('Error loading Agile & SCRUM Coach page:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'Agile & SCRUM Coach - Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Skab transparens, teamflow og psykologisk tryghed i komplekse ERP-programmer med René Vilsgaard som agil coach.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {language === 'da' ? 'YDELSE · AGILE WAYS OF WORKING' : language === 'en' ? 'SERVICE · AGILE WAYS OF WORKING' : 'DIENSTLEISTUNG · AGILE WAYS OF WORKING'}</span>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium text-navy-900 leading-tight">
            {pageData ? getLocalizedValue(pageData.title, language) : t.home.agileTitle}
          </h1>
          <p className="text-lg text-navy-900/60 font-sans">
            {t.services.agileSubtitle}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column - Image & Quick Facts */}
          <div className="lg:col-span-5 space-y-8">
            <div className="relative rounded-none overflow-hidden shadow-2xl border border-bluegray">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/agile-scrum-team.jpg"
                alt="Agilt Scrum Team Møde"
                className="w-full h-[350px] object-cover rounded-none"
              />
            </div>

            {/* Quick Facts Card */}
            <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 shadow-xl space-y-6">
              <h3 className="font-serif text-lg font-bold text-navy-900">{language === 'da' ? 'Agil eksekvering der virker' : language === 'en' ? 'Agile execution that works' : 'Agile Ausführung, die funktioniert'}</h3>
              <ul className="space-y-4 text-sm text-charcoal/80">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-petrol mt-0.5 shrink-0"></i>
                  <span><strong>{language === 'da' ? 'Psykologisk tryghed:' : language === 'en' ? 'Psychological safety:' : 'Psychologische Sicherheit:'}</strong> {language === 'da' ? 'René skaber et tillidsfuldt miljø, hvor teams tør udfordre og lære.' : language === 'en' ? 'René creates a trusting environment where teams dare to challenge and learn.' : 'René schafft ein vertrauensvolles Umfeld, in dem Teams es wagen, herauszufordern und zu lernen.'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-petrol mt-0.5 shrink-0"></i>
                  <span><strong>{language === 'da' ? 'Forudsigelig levering:' : language === 'en' ? 'Predictable delivery:' : 'Vorhersehbare Lieferung:'}</strong> {language === 'da' ? 'Går fra fragmenterede indsatser til en stabil og gennemsigtig kadence.' : language === 'en' ? 'Moves from fragmented efforts to a stable and transparent cadence.' : 'Wechselt von fragmentierten Bemühungen zu einer stabilen und transparenten Kadenz.'}</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-petrol mt-0.5 shrink-0"></i>
                  <span><strong>{language === 'da' ? 'Praktisk agilitet:' : language === 'en' ? 'Practical agility:' : 'Praktische Agilität:'}</strong> {language === 'da' ? 'Ingen dogmatisk teori – kun agile metoder, der skaber reel værdi i jeres hverdag.' : language === 'en' ? 'No dogmatic theory – only agile methods that create real value in your everyday life.' : 'Keine dogmatische Theorie – nur agile Methoden, die in Ihrem Alltag echten Mehrwert schaffen.'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Detailed Text */}
          <div className="lg:col-span-7 space-y-8 bg-white rounded-none border border-bluegray p-8 sm:p-10 shadow-xl">
            <div 
              className="prose prose-navy max-w-none text-charcoal/80 leading-relaxed space-y-6 font-sans text-base"
              dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : '' }}
            />

            {/* Call to Action */}
            <div className="pt-8 border-t border-bluegray/60 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="font-serif text-lg font-bold text-navy-900">{language === 'da' ? 'Vil du styrke jeres agile teamflow?' : language === 'en' ? 'Do you want to strengthen your agile team flow?' : 'Möchten Sie Ihren agilen Teamflow stärken?'}</h4>
                <p className="text-sm text-charcoal/60 mt-1">{language === 'da' ? 'Lad os tage en snak om, hvordan vi skaber momentum og tryghed.' : language === 'en' ? 'Let\'s have a chat about how we create momentum and safety.' : 'Lassen Sie uns darüber sprechen, wie wir Schwung und Sicherheit schaffen.'}</p>
              </div>
              <Link
                to="/kontakt"
                className="bg-navy-900 hover:bg-petrol text-offwhite px-6 py-3.5 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md whitespace-nowrap"
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
